import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'
import IconButton from '@mui/material/IconButton'
import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface TemplateMessageFiltersProps {
  onSearch: (term: string) => void
  onCategoryChange: (categories: string[]) => void
  onFinancingProgramChange: (program: string) => void
  onOriginSystemChange: (originSystem: string) => void
  selectedCategories: string[]
  selectedFinancingProgram: string
  selectedOriginSystem: string
  categories: readonly string[]
  categoryLabels: Record<string, string>
  financingPrograms: Record<string, string>
  originSystems: Record<string, string>
}

const TemplateMessageFilters = ({
  onSearch,
  onCategoryChange,
  onFinancingProgramChange,
  onOriginSystemChange,
  selectedCategories,
  selectedFinancingProgram,
  selectedOriginSystem,
  categories,
  categoryLabels,
  financingPrograms,
  originSystems,
}: TemplateMessageFiltersProps) => {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')


  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    onSearch(value)
  }

  const handleReset = () => {
    setSearchTerm('')
    onSearch('')
    onCategoryChange([])
    onFinancingProgramChange('')
    onOriginSystemChange('')
  }

  const handleCategoryChange = (_event: React.MouseEvent<HTMLElement>, newCategories: string[]) => {
    onCategoryChange(newCategories)
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('instantMessage.financingProgram')}</InputLabel>
          <Select
            value={selectedFinancingProgram}
            onChange={(e) => onFinancingProgramChange(e.target.value)}
            label={t('instantMessage.financingProgram')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {Object.entries(financingPrograms).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('instantMessage.originSystem')}</InputLabel>
          <Select
            value={selectedOriginSystem}
            onChange={(e) => onOriginSystemChange(e.target.value)}
            label={t('instantMessage.originSystem')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {Object.entries(originSystems).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          placeholder={t('instantMessage.searchPlaceholder')}
          value={searchTerm}
          onChange={handleSearchChange}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
            },
          }}
          size="small"
          variant="outlined"
          sx={{ flex: 1 }}
        />
        <Box>
          <IconButton
            onClick={handleReset}
            sx={{ height: 40, minWidth: 40, border: '1px solid #ccc' }}
            aria-label={t('common.reset')}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Stack>

      <Box>
        <ToggleButtonGroup
          value={selectedCategories}
          onChange={handleCategoryChange}
          sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2,
            '& .MuiToggleButtonGroup-grouped': {
              margin: '4px',
              '&:not(:first-of-type)': {
                borderRadius: '20px',
                marginLeft: '4px',
                borderLeft: '1px solid #e0e0e0',
              },
              '&:first-of-type': {
                borderRadius: '20px',
              },
            },
          }}
        >
          {categories.map((category) => (
            <ToggleButton
              key={category}
              value={category}
              sx={{
                textTransform: 'none',
                px: 3,
                py: 0.5,
                borderRadius: '20px',
                minWidth: '120px',
                height: '32px',
                border: '1px solid #e0e0e0',
                '&.Mui-selected': {
                  backgroundColor: '#FFD54F',
                  color: '#000',
                  fontWeight: 'bold',
                  border: '1px solid #FFD54F',
                  '&:hover': {
                    backgroundColor: '#FCC419',
                  },
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">
                  {categoryLabels[category]}
                </Typography>
              </Stack>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    </Stack>
  )
}

export default memo(TemplateMessageFilters)
